import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { KeyRound, Mail, ShieldCheck, Lock, Loader2, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXTS = {
  it: {
    title: 'Recupera Password',
    step1_title: 'Inserisci la tua email',
    step1_desc: 'Ti invieremo un codice di verifica per reimpostare la password.',
    step2_title: 'Inserisci il codice',
    step2_desc: 'Abbiamo inviato un codice a 6 cifre alla tua email.',
    step3_title: 'Nuova password',
    step3_desc: 'Scegli una nuova password sicura per il tuo account.',
    email: 'Email',
    codice: 'Codice di verifica',
    nuova_password: 'Nuova password',
    conferma_password: 'Conferma password',
    invia_codice: 'Invia codice',
    verifica: 'Verifica codice',
    reimposta: 'Reimposta password',
    torna_login: 'Torna al login',
    successo: 'Password reimpostata con successo!',
    vai_login: 'Vai al Login',
    password_mismatch: 'Le password non coincidono',
    password_short: 'La password deve avere almeno 6 caratteri',
  },
  en: {
    title: 'Recover Password',
    step1_title: 'Enter your email',
    step1_desc: 'We\'ll send you a verification code to reset your password.',
    step2_title: 'Enter the code',
    step2_desc: 'We sent a 6-digit code to your email.',
    step3_title: 'New password',
    step3_desc: 'Choose a new secure password for your account.',
    email: 'Email', codice: 'Verification code', nuova_password: 'New password',
    conferma_password: 'Confirm password', invia_codice: 'Send code', verifica: 'Verify code',
    reimposta: 'Reset password', torna_login: 'Back to login',
    successo: 'Password reset successfully!', vai_login: 'Go to Login',
    password_mismatch: 'Passwords do not match', password_short: 'Password must be at least 6 characters',
  },
  fr: {
    title: 'Récupérer le mot de passe',
    step1_title: 'Entrez votre email', step1_desc: 'Nous vous enverrons un code de vérification.',
    step2_title: 'Entrez le code', step2_desc: 'Nous avons envoyé un code à 6 chiffres à votre email.',
    step3_title: 'Nouveau mot de passe', step3_desc: 'Choisissez un nouveau mot de passe sécurisé.',
    email: 'Email', codice: 'Code de vérification', nuova_password: 'Nouveau mot de passe',
    conferma_password: 'Confirmer', invia_codice: 'Envoyer', verifica: 'Vérifier',
    reimposta: 'Réinitialiser', torna_login: 'Retour', successo: 'Mot de passe réinitialisé !',
    vai_login: 'Connexion', password_mismatch: 'Les mots de passe ne correspondent pas',
    password_short: 'Le mot de passe doit contenir au moins 6 caractères',
  },
  zh: {
    title: '找回密码', step1_title: '输入您的邮箱', step1_desc: '我们将发送验证码。',
    step2_title: '输入验证码', step2_desc: '我们已向您的邮箱发送了6位验证码。',
    step3_title: '新密码', step3_desc: '为您的账户选择一个新密码。',
    email: '邮箱', codice: '验证码', nuova_password: '新密码', conferma_password: '确认密码',
    invia_codice: '发送验证码', verifica: '验证', reimposta: '重置密码', torna_login: '返回登录',
    successo: '密码重置成功！', vai_login: '去登录', password_mismatch: '密码不匹配',
    password_short: '密码至少6个字符',
  },
  ru: {
    title: 'Восстановление пароля', step1_title: 'Введите email', step1_desc: 'Мы отправим код подтверждения.',
    step2_title: 'Введите код', step2_desc: 'Мы отправили 6-значный код на вашу почту.',
    step3_title: 'Новый пароль', step3_desc: 'Выберите новый надёжный пароль.',
    email: 'Эл. почта', codice: 'Код подтверждения', nuova_password: 'Новый пароль',
    conferma_password: 'Подтвердите пароль', invia_codice: 'Отправить', verifica: 'Проверить',
    reimposta: 'Сбросить', torna_login: 'Назад', successo: 'Пароль сброшен!', vai_login: 'Войти',
    password_mismatch: 'Пароли не совпадают', password_short: 'Пароль минимум 6 символов',
  },
};

const LOGO = "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png";

export default function PasswordResetPage() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const pt = (key) => TEXTS[lang]?.[key] || TEXTS['it']?.[key] || key;

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [codice, setCodice] = useState('');
  const [nuovaPassword, setNuovaPassword] = useState('');
  const [confermaPassword, setConfermaPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugCode, setDebugCode] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/password-reset/request`, { email });
      if (data.codice_debug) setDebugCode(data.codice_debug);
      toast.success('Codice inviato');
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!codice) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/verify`, { email, codice });
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (nuovaPassword.length < 6) { setError(pt('password_short')); return; }
    if (nuovaPassword !== confermaPassword) { setError(pt('password_mismatch')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/complete`, { email, codice, nuova_password: nuovaPassword });
      toast.success(pt('successo'));
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const stepConfig = [
    { num: 1, icon: Mail, color: '#0A84FF' },
    { num: 2, icon: ShieldCheck, color: '#FF9F0A' },
    { num: 3, icon: Lock, color: '#FF3B30' },
  ];

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 flex items-center justify-center px-4" data-testid="password-reset-page">
      <div className="w-full max-w-md">
        <div className="border border-[#B8D4E8] bg-white p-8">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <img src={LOGO} alt="LIFE LINKE" className="h-16 w-auto" />
          </div>

          <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A] text-center mb-6">
            <KeyRound size={18} className="inline mr-2 text-[#FF3B30]" />
            {pt('title')}
          </h1>

          {/* Step indicator */}
          {step < 4 && (
            <div className="flex items-center justify-center gap-2 mb-8" data-testid="step-indicator">
              {stepConfig.map(({ num, icon: Icon, color }) => (
                <div key={num} className="flex items-center gap-2">
                  <div className={`w-8 h-8 flex items-center justify-center border text-xs font-bold transition-all ${
                    step === num ? 'border-[#FF3B30] bg-[#FF3B30] text-white' :
                    step > num ? 'border-[#32D74B] bg-[#32D74B]/10 text-[#32D74B]' :
                    'border-[#B8D4E8] text-[#B8D4E8]'
                  }`}>
                    {step > num ? <CheckCircle2 size={14} /> : num}
                  </div>
                  {num < 3 && <div className={`w-8 h-0.5 ${step > num ? 'bg-[#32D74B]' : 'bg-[#B8D4E8]'}`} />}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 border border-[#FF3B30] bg-[#FF3B30]/10 flex items-center gap-2" data-testid="reset-error">
              <AlertTriangle size={14} className="text-[#FF3B30]" />
              <span className="text-xs text-[#FF3B30]">{error}</span>
            </div>
          )}

          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleRequestCode} className="space-y-4" data-testid="step-1-form">
              <p className="text-sm text-[#4A6B8A] text-center mb-4">{pt('step1_desc')}</p>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{pt('email')}</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="reset-email-input"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                data-testid="send-code-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {pt('invia_codice')}
              </button>
            </form>
          )}

          {/* Step 2: Code verification */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-4" data-testid="step-2-form">
              <p className="text-sm text-[#4A6B8A] text-center mb-2">{pt('step2_desc')}</p>
              {debugCode && (
                <div className="p-3 border border-[#0A84FF]/30 bg-[#0A84FF]/5 text-center" data-testid="debug-code">
                  <span className="text-[10px] text-[#4A6B8A] uppercase tracking-widest block mb-1">Codice di verifica</span>
                  <span className="text-2xl font-['JetBrains_Mono'] font-bold text-[#0A84FF] tracking-[0.3em]">{debugCode}</span>
                </div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{pt('codice')}</label>
                <input
                  type="text" value={codice} onChange={(e) => setCodice(e.target.value)} required
                  maxLength={6} placeholder="000000"
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono text-center text-2xl tracking-[0.3em]"
                  data-testid="reset-code-input"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                data-testid="verify-code-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {pt('verifica')}
              </button>
            </form>
          )}

          {/* Step 3: New password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4" data-testid="step-3-form">
              <p className="text-sm text-[#4A6B8A] text-center mb-4">{pt('step3_desc')}</p>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{pt('nuova_password')}</label>
                <input
                  type="password" value={nuovaPassword} onChange={(e) => setNuovaPassword(e.target.value)} required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{pt('conferma_password')}</label>
                <input
                  type="password" value={confermaPassword} onChange={(e) => setConfermaPassword(e.target.value)} required
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="confirm-password-input"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                data-testid="reset-password-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                {pt('reimposta')}
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 bg-[#32D74B]/10 border border-[#32D74B]/30 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-[#32D74B]" />
              </div>
              <p className="text-sm text-[#0D1B2A] font-bold">{pt('successo')}</p>
              <Link to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all text-sm"
                data-testid="goto-login-btn">
                {pt('vai_login')}
              </Link>
            </div>
          )}

          {/* Back to login */}
          {step < 4 && (
            <p className="mt-6 text-center">
              <Link to="/login" className="inline-flex items-center gap-1 text-xs text-[#4A6B8A] hover:text-[#FF3B30] uppercase tracking-widest" data-testid="back-to-login">
                <ArrowLeft size={12} />
                {pt('torna_login')}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
